import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = [
    "input",
    "preview",
    "message",
    "selectButton",
    "hiddenInput"
  ]

  static values = {
    paramScope: { type: String, default: "" },
    paramName: { type: String, default: "cover" },
    directUploadUrl: { type: String, default: "/rails/active_storage/direct_uploads" },
    accept: { type: String, default: "image/jpeg,image/png,image/webp" },
    maxSize: { type: Number, default: 10 * 1024 * 1024 },
    videoMaxSize: { type: Number, default: 100 * 1024 * 1024 },
    existing: { type: Object, default: {} },
    manageHidden: { type: Boolean, default: true }
  }

  connect() {
    this.file = null
    this.uploading = false
    this.currentObjectUrl = null
    this.form = this.element.closest("form")
    this.render()
    this.dispatch("connected")
  }

  disconnect() {
    this.revokeCurrentObjectUrl()
  }

  generateToken() {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }

  dispatchUploadEvent(name, token, extra = {}) {
    this.form?.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      detail: { token, ...extra }
    }))
  }

  preheatBlob(signedId) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (!signedId || !token) return

    fetch("/blobs/preheat", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token
      },
      body: JSON.stringify({ signed_id: signedId })
    }).catch(() => {})
  }

  select() {
    this.inputTarget.click()
  }

  changed() {
    const file = this.inputTarget.files?.[0]
    this.inputTarget.value = ""

    if (!file) return

    const message = this.validateFile(file)
    if (message) {
      this.showMessages([message])
      return
    }

    this.clearMessages()
    this.uploadFile(file)
  }

  validateFile(file) {
    const acceptedTypes = this.acceptValue.split(",").map((type) => type.trim()).filter(Boolean)

    if (acceptedTypes.length > 0 && !acceptedTypes.includes(file.type)) {
      if (this.acceptsVideo && file.type.startsWith("video/")) {
        return "Формат видео должен быть mp4 или mov"
      }
      return `Файл ${file.name} не подходит: выберите ${this.acceptLabel(acceptedTypes)}`
    }

    const maxSize = file.type.startsWith("video/") ? this.videoMaxSizeValue : this.maxSizeValue
    if (file.size > maxSize) {
      return `Файл ${file.name} больше ${this.humanSize(maxSize)}`
    }

    return null
  }

  get acceptsVideo() {
    return this.acceptValue.split(",").some((type) => type.trim().startsWith("video/"))
  }

  acceptLabel(types) {
    const map = {
      "image/jpeg": "jpeg",
      "image/png": "png",
      "image/webp": "webp",
      "video/mp4": "mp4",
      "video/quicktime": "mov"
    }
    const labels = types.map((type) => map[type] || type).join(", ")
    return labels
  }

  humanSize(bytes) {
    if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} МБ`
    if (bytes >= 1024) return `${Math.round(bytes / 1024)} КБ`
    return `${bytes} Б`
  }

  formatDuration(seconds) {
    const total = Math.round(seconds || 0)
    const mins = Math.floor(total / 60)
    const secs = total % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  uploadFile(file) {
    this.uploading = true
    this.file = file
    this.currentToken = this.generateToken()
    this.revokeCurrentObjectUrl()
    this.currentObjectUrl = URL.createObjectURL(file)
    this.render()
    this.dispatchUploadEvent("upload:started", this.currentToken)

    const delegate = {
      directUploadWillStoreFileWithXHR: (xhr) => this.trackUploadProgress(xhr)
    }

    new DirectUpload(file, this.directUploadUrlValue, delegate).create((error, blob) => {
      this.uploading = false

      if (error) {
        this.file = null
        this.revokeCurrentObjectUrl()
        this.showMessages([`Не удалось загрузить ${file.name}: ${error.message}`])
        this.render()
        this.dispatchUploadEvent("upload:finished", this.currentToken, { error })
        this.currentToken = null
        return
      }

      this.setExisting({
        signed_id: blob.signed_id,
        url: this.currentObjectUrl,
        content_type: file.type
      })

      this.preheatBlob(blob.signed_id)
      this.dispatchUploadEvent("upload:finished", this.currentToken)
      this.currentToken = null
    })
  }

  trackUploadProgress(xhr) {
    const bar = this.previewTarget.querySelector(".direct-upload-progress-bar")
    const text = this.previewTarget.querySelector(".direct-upload-progress-text")
    if (!bar || !text) return

    xhr.upload.addEventListener("progress", (event) => {
      const percent = event.lengthComputable ? Math.round(event.loaded / event.total * 100) : 0
      bar.style.width = `${percent}%`
      text.textContent = `Загрузка файла: ${percent}%`
    })

    xhr.upload.addEventListener("loadend", () => {
      bar.style.width = "100%"
      text.textContent = "Загрузка файла: 100%"
    })
  }

  setExisting(existing) {
    this.existingValue = existing
    this.updateHiddenInput()
    this.render()
    this.dispatch("uploaded", { detail: existing })
  }

  remove() {
    this.file = null
    this.existingValue = {}
    this.revokeCurrentObjectUrl()
    this.updateHiddenInput()
    this.render()
    this.dispatch("removed")
  }

  revokeCurrentObjectUrl() {
    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl)
      this.currentObjectUrl = null
    }
  }

  updateHiddenInput() {
    if (!this.hasHiddenInputTarget) return
    if (!this.manageHiddenValue) {
      this.hiddenInputTarget.innerHTML = ""
      return
    }

    this.hiddenInputTarget.innerHTML = ""

    const signedId = this.existingValue?.signed_id
    if (!signedId) return

    const input = document.createElement("input")
    input.type = "hidden"
    input.name = `${this.paramScopeValue}[${this.paramNameValue}]`
    input.value = signedId
    input.dataset.signedId = signedId
    this.hiddenInputTarget.appendChild(input)
  }

  render() {
    this.previewTarget.innerHTML = ""

    const existing = this.existingValue?.signed_id ? this.existingValue : null

    if (this.uploading) {
      this.previewTarget.appendChild(this.buildUploadingPreview())
    } else if (existing) {
      this.previewTarget.appendChild(this.buildExistingPreview(existing))
    } else {
      this.previewTarget.appendChild(this.buildPlaceholder())
    }

    this.updateSelectButton()
  }

  buildPlaceholder() {
    const el = document.createElement("button")
    el.type = "button"
    el.dataset.action = "single-upload#select"
    el.className = "w-full aspect-[16/9] rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-2 text-gray-500 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition overflow-hidden bg-gray-50"
    el.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
      <span class="text-sm font-medium">Перетащите или выберите файл</span>
      <span class="text-xs">${this.acceptHint()}</span>
    `
    return el
  }

  buildUploadingPreview() {
    const file = this.file
    const isVideo = file?.type?.startsWith("video/")
    const el = document.createElement("div")

    if (isVideo) {
      el.className = "relative w-full rounded-lg overflow-hidden bg-black"
      el.style.maxHeight = "240px"
      el.innerHTML = `
        <video src="${this.currentObjectUrl}" muted preload="metadata" playsinline class="w-full max-h-[240px] object-contain opacity-50"></video>
        <div class="absolute inset-0 flex items-center justify-center">
          <svg class="animate-spin h-8 w-8 text-[var(--color-primary)]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <div class="absolute bottom-0 left-0 right-0 px-2 pb-2">
          <div class="h-1.5 bg-black/30 rounded-full overflow-hidden">
            <div class="direct-upload-progress-bar h-full bg-[var(--color-primary)] w-0 transition-all duration-200"></div>
          </div>
          <div class="direct-upload-progress-text mt-1 text-center text-[10px] text-white drop-shadow">Загрузка файла: 0%</div>
        </div>
      `
    } else {
      el.className = "relative w-full aspect-[16/9] rounded-lg overflow-hidden bg-gray-100"
      el.innerHTML = `
        <img src="${this.currentObjectUrl}" class="w-full h-full object-cover opacity-50">
        <div class="absolute inset-0 flex items-center justify-center">
          <svg class="animate-spin h-8 w-8 text-[var(--color-primary)]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <div class="absolute bottom-0 left-0 right-0 px-2 pb-2">
          <div class="h-1.5 bg-black/30 rounded-full overflow-hidden">
            <div class="direct-upload-progress-bar h-full bg-[var(--color-primary)] w-0 transition-all duration-200"></div>
          </div>
          <div class="direct-upload-progress-text mt-1 text-center text-[10px] text-white drop-shadow">Загрузка файла: 0%</div>
        </div>
      `
    }
    return el
  }

  buildExistingPreview(existing) {
    const file = this.file
    const isVideo = existing.content_type?.startsWith("video/")
    const el = document.createElement("div")
    el.className = "relative w-full rounded-lg overflow-hidden bg-black group"

    if (isVideo) {
      el.style.maxHeight = "240px"
      el.innerHTML = `
        <video src="${existing.url}" controls playsinline preload="metadata" class="w-full max-h-[240px] object-contain"></video>
        <button type="button" data-action="single-upload#remove" class="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70">
          ×
        </button>
      `
      const info = this.buildFileInfo(existing)
      if (info) {
        const infoEl = document.createElement("div")
        infoEl.className = "mt-2 text-sm text-gray-600 flex flex-wrap items-center gap-x-3 gap-y-1"
        infoEl.innerHTML = info
        el.appendChild(infoEl)
      }

      const video = el.querySelector("video")
      const durationSpan = el.querySelector("[data-duration]")
      if (video && durationSpan) {
        video.addEventListener("loadedmetadata", () => {
          durationSpan.textContent = this.formatDuration(video.duration)
        })
      }
    } else {
      el.className += " aspect-[16/9]"
      el.innerHTML = `
        <img src="${existing.url}" class="w-full h-full object-cover">
        <button type="button" data-action="single-upload#remove" class="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70">
          ×
        </button>
      `
    }
    return el
  }

  buildFileInfo(existing) {
    const file = this.file
    const name = file?.name
    const size = file ? this.humanSize(file.size) : null

    const parts = []
    if (name) parts.push(`<span class="truncate max-w-full">${name}</span>`)
    parts.push(`<span data-duration></span>`)
    if (size) parts.push(`<span>${size}</span>`)

    return parts.join("")
  }

  acceptHint() {
    const acceptedTypes = this.acceptValue.split(",").map((type) => type.trim()).filter(Boolean)
    const labels = this.acceptLabel(acceptedTypes)
    const maxSize = this.maxSizeValue >= 1024 * 1024 ? `${this.maxSizeValue / (1024 * 1024)} МБ` : null
    const videoMaxSize = this.videoMaxSizeValue >= 1024 * 1024 ? `${this.videoMaxSizeValue / (1024 * 1024)} МБ` : null

    if (acceptedTypes.some((type) => type.startsWith("video/"))) {
      return `фото до ${maxSize}, видео до ${videoMaxSize}`
    }
    return `${labels}, до ${maxSize}`
  }

  updateSelectButton() {
    if (!this.hasSelectButtonTarget) return

    const existing = this.existingValue?.signed_id
    this.selectButtonTarget.textContent = existing ? "Заменить файл" : "Выбрать файл"
  }

  showMessages(messages) {
    if (!this.hasMessageTarget) return

    if (messages.length === 0) {
      this.clearMessages()
      return
    }

    this.messageTarget.textContent = [ ...new Set(messages) ].join("; ")
    this.messageTarget.classList.remove("hidden")
  }

  clearMessages() {
    if (!this.hasMessageTarget) return

    this.messageTarget.textContent = ""
    this.messageTarget.classList.add("hidden")
  }
}
